import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';

import { defaultWMS1141_R01HeaderDto, WMS1141_R01HeaderDto } from './Dto_WMS1141_R01';
import { useWMS1141_R01Store } from './Store_WMS1141_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'WMS1141_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1141_R01HeaderDto> | null>;
	handleCreateClick: () => void;
	defaultWMS1141_R01HeaderRow: WMS1141_R01HeaderDto;
}

export const Table_WMS1141_R01 = (props: Props) => {
	const { gridRef, handleCreateClick, defaultWMS1141_R01HeaderRow } = props;

	const {
		WMS1141_R01List,
		selectedWMS1141_R01,
		setSelectedWMS1141_R01,
		initSelectedWMS1141_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useWMS1141_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'transportNo',
		gridData: WMS1141_R01List,
		defaultGridData: defaultWMS1141_R01HeaderDto,
		selectedRow: selectedWMS1141_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compNm: '사업장',
		orderDt: '입고일자',
		customNm: '상호',
		orderAmt: '매출금액',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<WMS1141_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '입고일자', colId: 'transportDt', field: 'transportDt', width: 90,
			valueFormatter: (p: { data?: WMS1141_R01HeaderDto }) =>
				p.data?.transportDt ? formatDate(new Date(p.data.transportDt)) : '',
			comparator: cellDateComparator,
			cellStyle: (p) => getCellStyle(p, 'transportDt'),
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
		},
	], [headerNames]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS1141_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS1141_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<WMS1141_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedWMS1141_R01?.transportNo === rowData.transportNo;
		if (isSelected) {
			setSelectedWMS1141_R01(undefined);
			handleCreateClick();
		} else {
			setSelectedWMS1141_R01(rowData);
		}
		setDetailScrollTop(null);
	}, [selectedWMS1141_R01, setSelectedWMS1141_R01, handleCreateClick, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: `${WMS1141_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [WMS1141_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [WMS1141_R01List, selectedWMS1141_R01]);

	return (
		<GridContextWrapper<WMS1141_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedWMS1141_R01}
			defaultRowData={defaultWMS1141_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<WMS1141_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<WMS1141_R01HeaderDto>({
					idKey: 'transportNo',
					selectedList: selectedWMS1141_R01 ? [selectedWMS1141_R01.transportNo] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
